/**
 * Turns a hard position reset — a restart teleport, `Match.resetSerial` incrementing — into a
 * short eased glide, so the renderer is never asked to draw a player cutting instantly across
 * the pitch with nothing in between.
 *
 * Deliberately separate from `Vector3Smoother` (`core/smoother.ts`): that interpolates a few
 * tens of milliseconds of ordinary simulation jitter and must never overshoot, where this
 * bridges a jump that can be tens of metres and is meant to be seen crossing the screen.
 * Feeding a teleport through the jitter smoother is exactly what used to make the render
 * position jump straight to the one new sample with nothing to interpolate against — a player
 * frozen for the whole stoppage, then cut instantly to his restart mark on the very next
 * rendered frame. This does not give him a walking gait; it only removes the cut. See the
 * entry in `PENDING.md` for why a real walk-to-your-mark is a separate, larger piece of work.
 */

import { Vector3 } from 'three';

/** Long enough to read as motion, short enough that a 2-8 second stoppage never feels slow. */
const GLIDE_MS = 400;

/** Smoothstep: zero velocity at both ends, so the glide neither jumps off nor snaps to a stop. */
function easedProgress(t: number): number {
  const clamped = Math.min(Math.max(t, 0), 1);
  return clamped * clamped * (3 - 2 * clamped);
}

export class ResetGlide {
  private readonly from = new Vector3();
  private readonly to = new Vector3();
  private startMs = -Infinity;

  /** Begin gliding from wherever the player was actually being shown to the new mark. */
  begin(from: Vector3, to: Vector3, atMs: number): void {
    this.from.copy(from);
    this.to.copy(to);
    this.startMs = atMs;
  }

  /** The eased position, or null once the glide has run its course. */
  sample(atMs: number): Vector3 | null {
    const t = (atMs - this.startMs) / GLIDE_MS;
    if (t >= 1) return null;
    return this.from.clone().lerp(this.to, easedProgress(t));
  }
}
